import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useMoney } from "@units/useMoney";
import { fmtPct } from "@lib/format";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetPadRollupQuery } from "@api/pApi";
import { usePadSite } from "./usePadSite";
import { PadCostSection } from "./PadCostSection";
import { PadTimeSection } from "./PadTimeSection";
import "./pad.css";

/**
 * Pad view (§12) — a pad-level rollup of time & cost across all the wells on a
 * pad, plus the shared/pad costs (mob-demob, pad construction, rig moves,
 * shared services) and how they allocate to the wells.
 *
 * Hierarchy: Pad = a Site in the Oxbo-M register (…Yacimiento → Pad/Site →
 * Well → Event → Report). Every number — allocated-shared, per-well split,
 * rig utilization — is server-computed by Oxbo-P; the FE only lays it out.
 */
export function Pad() {
  const { t, locale } = useLocale();
  const { money } = useMoney();
  const { siteId } = usePadSite();

  const { data: roll, isLoading, isError, refetch } = useGetPadRollupQuery(
    siteId ?? skipToken,
  );

  const ccy = roll?.currency ?? null;
  // `per_well` can be absent on a partial/error body even when `roll` is truthy.
  const allocated = (roll?.per_well ?? []).reduce((s, w) => s + w.allocated_shared, 0);

  const stats: Stat[] = roll
    ? [
        { label: t("pad.kpi.totalCost"), value: money(roll.pad_total_cost, ccy, { compact: true }) },
        {
          label: t("pad.kpi.sharedCost"),
          value: money(roll.shared_cost, ccy, { compact: true }),
          tone: "ops",
        },
        {
          label: t("pad.kpi.allocated"),
          value: money(allocated, ccy, { compact: true }),
        },
        { label: t("pad.kpi.padDays"), value: `${roll.pad_days}`, sub: t("pad.kpi.days") },
        {
          label: t("pad.kpi.rigUtil"),
          value: fmtPct(roll.rig_utilization_pct, locale),
          tone: roll.rig_utilization_pct != null && roll.rig_utilization_pct < 70 ? "warn" : "good",
        },
      ]
    : [];

  const context = roll
    ? `${roll.pad_name} · ${roll.field} · ${t("pad.wellCount", { n: roll.well_count })}`
    : t("well.notSelected");

  return (
    <div>
      <div className="page-head">
        <h1>{t("pad.title")}</h1>
        <p className="subtitle">{context}</p>
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!roll}
        emptyMessage={t("pad.noData")}
        onRetry={refetch}
      >
        {roll ? (
          <>
            <StatBand stats={stats} />
            <PadCostSection siteId={roll.site_id} rollup={roll} />
            <PadTimeSection siteId={roll.site_id} rollup={roll} />
          </>
        ) : null}
      </QueryState>
    </div>
  );
}
